import { DataType } from '../../../universal-table/types/column.types'
import { GridColumn, GridRendererType } from '../../types/grid.types'
import { TextCell, MutedCell } from './TextCell'
import { NumCell, QtyCell } from './NumCell'
import MoneyCell from './MoneyCell'
import DateCell from './DateCell'
import CheckboxCell from './CheckboxCell'
import BadgeCell from './BadgeCell'
import ClientCell from './ClientCell'

export interface GridCellRendererProps<T> {
  column: GridColumn<T>
  row: T
}

const inferRenderer = (dataType: DataType, hasCurrency: boolean): GridRendererType => {
  switch (dataType) {
    case 'number':
      return 'num'
    case 'float':
      return hasCurrency ? 'money' : 'num'
    case 'date':
      return 'date'
    case 'boolean':
      return 'checkbox'
    default:
      return 'text'
  }
}

export const GridCellRenderer = <T,>({ column, row }: GridCellRendererProps<T>) => {
  const value = row[column.key]

  const toText = (): string => {
    if (column.formatValue) return column.formatValue(value)
    if (column.labelResolver) return column.labelResolver(value)
    return value == null ? '' : String(value)
  }

  const renderer =
    column.cellRendererType ?? inferRenderer(column.dataType, !!column.currencySuffix)

  switch (renderer) {
    case 'checkbox':
      return <CheckboxCell checked={Boolean(value)} />
    case 'badge': {
      const badge = column.badgeResolver?.(row) ?? { label: toText(), color: '#757575' }
      return <BadgeCell label={badge.label} color={badge.color} />
    }
    case 'client': {
      const sub = column.subKey != null ? String(row[column.subKey] ?? '') : undefined
      return <ClientCell name={toText()} sub={sub} />
    }
    case 'date':
      return <DateCell value={value} />
    case 'num':
      return <NumCell>{toText()}</NumCell>
    case 'money':
      return <MoneyCell value={value} suffix={column.currencySuffix} />
    case 'qty':
      return <QtyCell>{toText()}</QtyCell>
    case 'muted':
      return <MutedCell>{toText()}</MutedCell>
    case 'text':
    default:
      return <TextCell>{toText()}</TextCell>
  }
}

export default GridCellRenderer
